/* ──────────────────────────────────────────────
   src/hooks/useFestivos.ts
   Compatible con TanStack Query v5
   ────────────────────────────────────────────── */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/axios';
import type { Festivo } from '../types/entities';

// Re-exportar tipo para compatibilidad
export type { Festivo };

export function useFestivos(year?: number) {
  const qc = useQueryClient();
  const KEY = year ? ['festivos', year] : ['festivos']; // cache por año si se filtra

  /* ---------- 1) listado ---------- */
  const { data = [], isFetching: loading } = useQuery({
    queryKey: KEY,
    queryFn: async () => {
      const params = year ? { year: year.toString() } : {};
      const { data } = await api.get('/festivos', { params });
      return data as Festivo[];
    },
  });

  const invalidate = () => {
    // Invalidar todas las queries de festivos (con y sin año)
    qc.invalidateQueries({ queryKey: ['festivos'] });
  };

  /* ---------- 2) mutaciones ---------- */
  const create = useMutation({
    mutationFn: (body: Festivo) => api.post('/festivos', body),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: (body: Festivo) => api.put(`/festivos/${body.fecha}`, body),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (fecha: string) => api.delete(`/festivos/${fecha}`),
    onSuccess: invalidate,
  });

  return { rows: data, loading, create, update, remove };
}

/**
 * Hook para verificar si una fecha específica es festivo
 */
export function useCheckFestivo(fecha: string | null) {
  return useQuery({
    queryKey: ['festivos', 'check', fecha],
    queryFn: async () => {
      if (!fecha) return { esFestivo: false, festivo: null };
      const { data } = await api.get(`/festivos/check/${fecha}`);
      return data as { fecha: string; esFestivo: boolean; festivo: Festivo | null };
    },
    enabled: !!fecha,
  });
}
